import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDayPass } from '@/contexts/DayPassContext';
import { Button } from '@/components/ui/button';
import { Calendar, Check, Sparkles } from 'lucide-react';
import { OptimizedImage } from '@/components/OptimizedImage';

const DayPassPricingSection: React.FC = () => {
    const { t } = useLanguage();
    const { setIsTrayOpen } = useDayPass();

    const inclusions = [
        {
            id: 'amenities',
            text: t('activities.dayPass.dayPassPricing.inclusions.amenities'),
        },
        {
            id: 'massage',
            text: t('activities.dayPass.dayPassPricing.inclusions.massage'),
        },
        {
            id: 'meal',
            text: t('activities.dayPass.dayPassPricing.inclusions.meal'),
        },
        {
            id: 'cafe',
            text: t('activities.dayPass.dayPassPricing.inclusions.cafe'),
        },
        {
            id: 'sauna',
            text: t('activities.dayPass.dayPassPricing.inclusions.sauna'),
        },
    ];


    return (
        <section className="relative bg-pebble-beige py-20 md:py-24 px-4 md:px-6 lg:px-0 overflow-hidden">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
                <OptimizedImage
                    src="/images/Homepage Shortlisted media/home hero image pc.webp"
                    alt="Beatenberg Resort & Spa"
                    className="w-full h-full object-cover"
                    priority={false}
                />
                {/* Overlay for text readability - lighter overlay since we have a light background */}
                <div className="absolute inset-0 bg-pebble-beige/85" />
                <div className="absolute inset-0 bg-gradient-to-b from-pebble-beige/60 via-pebble-beige/80 to-pebble-beige/90" />
            </div>

            {/* Background Ornament - Subtle Backdrop */}
            <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-[url('/ornament-gold.svg')] bg-no-repeat bg-right-top opacity-[0.02] pointer-events-none z-0" />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12 md:mb-16"
                >
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-playfair text-charcoal-gray mb-4 leading-tight">
                        {t('activities.dayPass.dayPassPricing.title')}
                    </h2>
                    <p className="text-lg md:text-xl text-charcoal-gray/80 font-lato leading-relaxed max-w-3xl mx-auto">
                        {t('activities.dayPass.dayPassPricing.subtitle')}
                    </p>
                </motion.div>

                {/* Main Pricing Card */}
                <div className="flex justify-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="w-full max-w-2xl"
                    >
                        {/* Price Display */}
                        <div className="bg-white/90 backdrop-blur-sm border border-alpine-gold/30 rounded-2xl p-8 shadow-xl">
                            <div className="flex items-baseline justify-between mb-6">
                                <div>
                                    <p className="text-alpine-gold font-lato text-sm uppercase tracking-widest font-bold mb-2">
                                        {t('activities.dayPass.dayPassPricing.priceLabel')}
                                    </p>
                                    <h3 className="text-4xl md:text-5xl font-playfair text-charcoal-gray">
                                        {t('activities.dayPass.dayPassPricing.price')}
                                    </h3>
                                    <p className="text-charcoal-gray/60 font-lato text-sm mt-2">
                                        {t('activities.dayPass.dayPassPricing.perPerson')}
                                    </p>
                                </div>
                                <div className="w-16 h-16 bg-alpine-gold/10 rounded-xl flex items-center justify-center">
                                    <Sparkles className="w-8 h-8 text-alpine-gold" />
                                </div>
                            </div>

                            {/* Inclusions List */}
                            <div className="space-y-4 pt-6 border-t border-alpine-gold/20">
                                <h4 className="text-charcoal-gray font-playfair text-lg mb-4">
                                    {t('activities.dayPass.dayPassPricing.inclusionsTitle')}
                                </h4>
                                {inclusions.map((inclusion, index) => (
                                    <motion.div
                                        key={inclusion.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                        className="flex items-start gap-3"
                                    >
                                        <div className="w-6 h-6 rounded-full bg-alpine-gold/20 flex items-center justify-center shrink-0 mt-0.5">
                                            <Check className="w-4 h-4 text-alpine-gold" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-charcoal-gray font-lato text-base leading-relaxed">
                                                {inclusion.text}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* CTA Button */}
                            <div className="pt-6 mt-6 border-t border-alpine-gold/20">
                                <Button
                                    onClick={() => {
                                        setIsTrayOpen(true, 'pricing_section');
                                    }}
                                    className="w-full bg-alpine-gold hover:bg-alpine-gold/90 text-white px-6 py-6 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                                    trackingType="none"
                                    data-oid="book-day-pass-btn"
                                >
                                    <Calendar size={20} className="mr-2" />
                                    {t('activities.dayPass.dayPassPricing.cta')}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default DayPassPricingSection;
